import { useRef } from 'react';
import { span, typed, useReducedMotion, useScrollProgress } from '../motion.ts';
import { useEffect, useState } from 'react';

/**
 * Three steps, as three scenes of one picture. The words are the page's own
 * (unchanged); what is new is that each step is shown while it is being read —
 * a page being read, a journey being wired and approved, a buyer leading it —
 * and the second scene is still on the stage in the third, because the buyer
 * is walking the story the team just set.
 *
 * Like the section above it, it is a tall section with a sticky stage and one
 * number (scroll progress) that everything is a function of. With reduced
 * motion it is the original three rows.
 */
const STEPS = [
  ['01', 'Record your product.', 'Walk through the screens you want buyers to see. Persivo captures them into a replayable copy.', 'Your product is the source.'],
  ['02', 'Make it your story.', 'Connect the journey, write the key moments, and approve what the agent is allowed to say.', 'Your team sets the direction.'],
  ['03', 'Let the buyer lead.', 'The agent guides the conversation through your recorded product, starting with what the buyer came to understand.', 'Every question has somewhere to go.'],
] as const;

const W = 560, H = 440;
const NODES = [
  { id: 'A', name: 'Opening', x: 18, y: 170 },
  { id: 'B', name: 'Funnels', x: 214, y: 50 },
  { id: 'C', name: 'Audiences', x: 214, y: 290 },
  { id: 'D', name: 'Activation', x: 410, y: 170 },
] as const;
const NW = 132, NH = 84;
const WIRES = [['A', 'B'], ['A', 'C'], ['B', 'D'], ['C', 'D']] as const;
const at = (id: string) => NODES.find(n => n.id === id)!;
const wire = ([a, b]: readonly [string, string]) => {
  const p = at(a), q = at(b), x1 = p.x + NW, y1 = p.y + NH / 2, x2 = q.x, y2 = q.y + NH / 2, mx = (x1 + x2) / 2;
  return `M${x1} ${y1} C${mx} ${y1} ${mx} ${y2} ${x2} ${y2}`;
};
const Q = 'Can I compare different audiences?';

export function HowItWorks() {
  const reduced = useReducedMotion();
  const pin = useRef<HTMLElement>(null);
  const box = useRef<HTMLDivElement>(null);
  const p = useScrollProgress(pin);
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const fit = () => setScale(Math.min(1, el.clientWidth / W));
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, [reduced]);

  if (reduced) {
    return <section className="process-section wrap" id="how">
      <div className="section-heading"><p className="eyebrow">FROM YOUR PRODUCT TO THEIR FIRST DEMO</p><h2 className="section-title">Set the story.<br /><em>Let the conversation unfold.</em></h2></div>
      <div className="process-grid">{STEPS.map(([n, title, description, note]) => <article key={n}><div className="process-index"><span>{n}</span></div><h3>{title}</h3><div className="process-description"><p>{description}</p><span className="process-note">{note}</span></div></article>)}</div>
    </section>;
  }

  const step = Math.min(2, Math.floor(p * 3));
  const t = p * 3 - step;                                            // 0..1 inside the step
  const scan = step === 0 ? t : 1;
  const screens = Math.round(14 * span(step === 0 ? t : 1, 0.05, 0.85));
  const wired = step === 1 ? span(t, 0.05, 0.72) : step > 1 ? 1 : 0;           // how much of the journey is drawn
  const approved = step === 1 ? span(t, 0.62, 0.8) : step > 1 ? 1 : 0;
  const q = step === 2 ? span(t, 0.05, 0.42) : 0;
  const lead = step === 2 ? span(t, 0.46, 0.7) : 0;                            // the pointer's trip from Funnels to Audiences
  const said = step === 2 ? span(t, 0.7, 0.8) : 0;
  const px = lerp(at('B').x + NW * 0.7, at('C').x + NW * 0.6, ease(lead)), py = lerp(at('B').y + NH * 0.6, at('C').y + NH * 0.45, ease(lead));

  return <section className="how" id="how" ref={pin} data-step={step} aria-labelledby="how-title">
    <div className="how-stage wrap">
      <div className="how-copy">
        <p className="eyebrow">FROM YOUR PRODUCT TO THEIR FIRST DEMO</p>
        <h2 id="how-title" className="section-title">Set the story.<br /><em>Let the conversation unfold.</em></h2>
        <ol className="how-steps">
          {STEPS.map(([n, title, description, note], i) => (
            <li key={n} data-state={i < step ? 'done' : i === step ? 'now' : 'next'}>
              <span className="how-n">{n}</span>
              <div><h3>{title}</h3><div className="how-more"><p>{description}</p><span className="process-note">{note}</span></div></div>
              <i className="how-bar"><b style={{ ['--f' as string]: i < step ? 1 : i === step ? t : 0 }}/></i>
            </li>
          ))}
        </ol>
      </div>
      <div className="how-visual" ref={box} aria-hidden="true" style={{ height: H * scale }}>
        <div className="hv" style={{ width: W, height: H, transform: `scale(${scale})` }} data-step={step}>
          {/* 01 — a page, being read */}
          <div className="hv-win">
            <div className="hv-bar"><i/><i/><i/><span>yourproduct.com / app</span></div>
            <div className="hv-page"><div className="hv-side"><u/><u/><u/><u/><u/></div><div className="hv-main"><b/><div className="hv-cards"><u/><u/><u/></div><div className="hv-chart"><u/><u/><u/><u/><u/><u/></div></div></div>
            <i className="hv-scan" style={{ top: 34 + scan * 292 }}/>
          </div>
          <div className="hv-chips">
            <span data-on={screens > 0 ? '' : undefined}><b>{screens}</b> screens captured</span>
            <span data-on={step === 0 && t > 0.55 || step > 0 ? '' : undefined}>Scripts removed</span>
            <span data-on={step === 0 && t > 0.78 || step > 0 ? '' : undefined}>Network blocked</span>
          </div>

          {/* 02 + 03 — the journey, then the buyer walking it */}
          <svg className="hv-wires" width={W} height={H} viewBox={`0 0 ${W} ${H}`}>
            {WIRES.map((w, i) => <path key={i} d={wire(w)} pathLength={1} style={{ strokeDashoffset: 1 - span(wired, i * 0.2, i * 0.2 + 0.4) }}/>)}
          </svg>
          {NODES.map((n, i) => (
            <div key={n.id} className={'hv-node' + (step === 2 && n.id === 'C' && lead > 0.85 ? ' hot' : '')} style={{ left: n.x, top: n.y, width: NW, height: NH, ['--k' as string]: span(wired, i * 0.2, i * 0.2 + 0.25) }}>
              <small>{n.name}</small><u/><u/><u/>
            </div>
          ))}
          <div className="hv-claims" data-on={approved > 0 ? '' : undefined}><span className="hv-tick">✓</span> You approve every claim <em>{Math.round(3 * approved)} of 3</em></div>

          <div className="hv-pill"><span className="hs-avatar">M</span><span className="sv-pq">{typed(Q, q)}{step === 2 && q > 0 && q < 1 && <i className="hs-caret"/>}</span></div>
          <div className="hv-pointer" style={{ left: px, top: py }}><svg viewBox="0 0 24 28"><path d="M3 2v21l6-6 5 9 4-2-5-9h9Z"/></svg><span>Persivo</span></div>
          <div className="hv-say" data-on={said > 0 ? '' : undefined}><img src="/agent-thinking.svg" alt="" width="34" height="34"/><p>Here’s the audience comparison.</p></div>
        </div>
      </div>
    </div>
    <ol className="sr-only">{STEPS.map(([n, title, description]) => <li key={n}>{title} {description}</li>)}</ol>
  </section>;
}

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const ease = (t: number) => (t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2);
